import { ObjectType } from '../Constants';
import SimpleObject from './SimpleObject';

/** @typedef { import('./types').Transcript } Transcript */

/**
 * @module Chapter
 */
export default class Chapter extends SimpleObject {
  /**
   * Creates a new instance of a Chapter.
   * @param {Object} data
   * @param {String} [data.id] - Chapter ID
   * @param {Number} [data.duration] - Optional parameter
   * @param {Number} data.startOffset
   * @param {String} data.title
   * @param {Transcript} [data.transcript]
   * @param {String} [data.mp3Url] - MP3 URL for this chapter (used by MP3 Rewind streamer)
   * @param {Boolean} [data.isMP3] - Whether this chapter is backed by a downloadable MP3 (MP3 Rewind streamer)
   */
  constructor({ id, duration = 0, startOffset, title, transcript, mp3Url, isMP3 = false }) {
    super(ObjectType.CHAPTER);
    this.data = { id, duration, startOffset, title, transcript, mp3Url, isMP3 };
  }

  /**
   * The chapter duration
   * @returns {Number}
   */
  getDuration() {
    return this?.data?.duration || 0;
  }

  /**
   * The chapter startOffset
   * @returns {Number}
   */
  getStartOffset() {
    return this.data.startOffset;
  }

  /**
   * The chapter title
   * @returns {String}
   */
  getTitle() {
    return this.data.title;
  }

  /**
   * The chapter transcripts
   * @returns {Transcript}
   */
  getTranscript() {
    return this.data.transcript;
  }

  /**
   * The MP3 audio URL for this chapter (MP3 Rewind streamer only).
   * @returns {String | undefined}
   */
  getAudioUrl() {
    return this.data.mp3Url;
  }

  /**
   * The chapter ID
   * @returns {String | undefined}
   */
  getId() {
    return this.data.id;
  }

  /**
   * Whether this chapter is backed by a downloadable MP3 (MP3 Rewind streamer only).
   * @returns {Boolean}
   */
  getIsMP3() {
    return !!this.data.isMP3;
  }
}
